// src/components/agency/AgencyScopeProvider.tsx
"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { useSession } from "@/app/SessionProvider";
import { authFetch } from "@/utils/authFetch";
import { EffectiveUserContext } from "@/components/admin/EffectiveUserProvider";

// Optional hook for View As support - safely access EffectiveUserContext
// EffectiveUserContext defaults to null when not wrapped in EffectiveUserProvider
function useEffectiveUserSafe() {
  const ctx = useContext(EffectiveUserContext);
  return ctx ? { effectiveUserId: ctx.effectiveUserId } : { effectiveUserId: null };
}

type ClientRow = {
  id: string;
  agency_id: string;
  name: string;
  status?: string;
  created_at?: string;
};

type ViewScopeMode = "personal" | "agency" | "client";

type AgencyScope = {
  // membership
  isAgencyMember: boolean;
  agencyId: string | null;
  agencyRole: 'owner' | 'admin' | 'member' | null;

  // list of clients in the agency
  clients: ClientRow[];

  /**
   * What we're currently looking at in dashboards (analytics, generated content, etc.)
   *  - "personal": just my own stuff (filter by user_id)
   *  - "agency": the whole agency (filter by agency_id)
   *  - "client": a specific client (filter by that client_id)
   */
  viewMode: ViewScopeMode;

  /**
   * If viewMode === 'client', which client is selected?
   * Otherwise null.
   */
  selectedClientId: string | null;

  // UI helpers
  setViewPersonal: () => void;
  setViewAgencyWide: () => void;
  setViewSpecificClient: (clientId: string | null) => void;

  // old compat function used by some existing buttons ("View Content" etc.)
  setSelectedClientId: (id: string | null) => void;

  refreshClients: () => Promise<void>;
};

const AgencyScopeContext = createContext<AgencyScope>({
  isAgencyMember: false,
  agencyId: null,
  agencyRole: null,
  clients: [],
  viewMode: "personal",
  selectedClientId: null,
  setViewPersonal: () => {},
  setViewAgencyWide: () => {},
  setViewSpecificClient: () => {},
  setSelectedClientId: () => {},
  refreshClients: async () => {},
});

export const AgencyScopeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useSession();
  const { effectiveUserId } = useEffectiveUserSafe();

  const [isAgencyMember, setIsAgencyMember] = useState(false);
  const [agencyId, setAgencyId] = useState<string | null>(null);
  const [agencyRole, setAgencyRole] = useState<'owner' | 'admin' | 'member' | null>(null);
  const [clients, setClients] = useState<ClientRow[]>([]);

  const [viewMode, setViewMode] = useState<ViewScopeMode>("personal");
  const [selectedClientId, _setSelectedClientId] = useState<string | null>(null);

  // persist per-user so when they come back, we remember
  const storageBase = user
    ? `ssp:agencyScope:${user.id}:`
    : "ssp:agencyScope:anon:";
  const storageViewKey = storageBase + "viewMode";
  const storageClientKey = storageBase + "selectedClientId";

  const loadScopeFromApi = useCallback(async (effectiveUserId?: string | null) => {
    if (!user) {
      setIsAgencyMember(false);
      setAgencyId(null);
      setAgencyRole(null);
      setClients([]);
      return;
    }

    try {
      // Support View As functionality - pass effectiveUserId if provided
      const url = effectiveUserId 
        ? `/api/agency/bootstrap?effectiveUserId=${encodeURIComponent(effectiveUserId)}`
        : "/api/agency/bootstrap";
      
      // Use authFetch to include Authorization header (needed for GHL iframe auth)
      const res = await authFetch(url, {
        method: "GET",
      });

      if (!res.ok) {
        console.error(
          "[AgencyScopeProvider] /api/agency/bootstrap failed:",
          res.status
        );
        setIsAgencyMember(false);
        setAgencyId(null);
        setAgencyRole(null);
        setClients([]);
        return;
      }

      const json = await res.json();

      // Debug: log what we received
      console.log("[AgencyScopeProvider] Bootstrap API response:", {
        isAgencyMember: json.isAgencyMember,
        agencyId: json.agencyId,
        clientsCount: Array.isArray(json.clients) ? json.clients.length : 0,
        clients: Array.isArray(json.clients) ? json.clients : null,
      });

        setIsAgencyMember(!!json.isAgencyMember && !!json.agencyId);
        setAgencyId(json.agencyId ?? null);
        setAgencyRole(json.agencyRole || null);
        const clientsList = Array.isArray(json.clients) ? json.clients : [];
        setClients(clientsList);
      
      if (clientsList.length === 0 && json.agencyId) {
        console.warn("[AgencyScopeProvider] ⚠️ Agency member but received empty clients array from API");
      }
    } catch (err) {
      console.error("[AgencyScopeProvider] bootstrap error:", err);
      setIsAgencyMember(false);
      setAgencyId(null);
      setClients([]);
    }
  }, [user]);

  // Initialize membership + clients from server API
  // Re-load when effectiveUserId changes (for View As support)
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    loadScopeFromApi(effectiveUserId);
  }, [loadScopeFromApi, effectiveUserId]);

  // restore persisted view selection — sets state to sync with localStorage, which is intentional
  useEffect(() => {
    if (!isAgencyMember) {
      // if you're not in an agency, force personal mode
      setViewMode("personal");
      _setSelectedClientId(null);
      return;
    }

    if (typeof window === "undefined") return;

    const savedMode = window.localStorage.getItem(
      storageViewKey
    ) as ViewScopeMode | null;
    const savedClient = window.localStorage.getItem(storageClientKey);

    if (savedMode === "client" && savedClient) {
      setViewMode("client");
      _setSelectedClientId(savedClient);
    } else if (savedMode === "agency") {
      setViewMode("agency");
      _setSelectedClientId(null);
    } else {
      // default
      setViewMode("personal");
      _setSelectedClientId(null);
    }
  }, [isAgencyMember, storageViewKey, storageClientKey]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // helpers to update + persist
  const setViewPersonal = useCallback(() => {
    setViewMode("personal");
    _setSelectedClientId(null);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(storageViewKey, "personal");
      window.localStorage.removeItem(storageClientKey);
    }
  }, [storageViewKey, storageClientKey]);

  const setViewAgencyWide = useCallback(() => {
    setViewMode("agency");
    _setSelectedClientId(null);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(storageViewKey, "agency");
      window.localStorage.removeItem(storageClientKey);
    }
  }, [storageViewKey, storageClientKey]);

  const setViewSpecificClient = useCallback(
    (clientId: string | null) => {
      if (clientId) {
        setViewMode("client");
        _setSelectedClientId(clientId);
        if (typeof window !== "undefined") {
          window.localStorage.setItem(storageViewKey, "client");
          window.localStorage.setItem(storageClientKey, clientId);
        }
      } else {
        // if null -> treat like agency wide
        setViewAgencyWide();
      }
    },
    [setViewAgencyWide, storageViewKey, storageClientKey]
  );

  // backwards compat for existing calls like onViewContent(id)
  const setSelectedClientId = useCallback(
    (clientId: string | null) => {
      setViewSpecificClient(clientId);
    },
    [setViewSpecificClient]
  );

  const refreshClients = useCallback(async () => {
    // simply re-run the bootstrap, which refreshes clients as well
    await loadScopeFromApi(effectiveUserId);
  }, [loadScopeFromApi, effectiveUserId]);

  const value = useMemo(
    () => ({
      isAgencyMember,
      agencyId,
      agencyRole,
      clients,
      viewMode,
      selectedClientId,
      setViewPersonal,
      setViewAgencyWide,
      setViewSpecificClient,
      setSelectedClientId,
      refreshClients,
    }),
    [
      isAgencyMember,
      agencyId,
      agencyRole,
      clients,
      viewMode,
      selectedClientId,
      setViewPersonal,
      setViewAgencyWide,
      setViewSpecificClient,
      setSelectedClientId,
      refreshClients,
    ]
  );

  return (
    <AgencyScopeContext.Provider value={value}>
      {children}
    </AgencyScopeContext.Provider>
  );
};

export const useAgencyScope = () => useContext(AgencyScopeContext);
